import { html, nothing } from 'lit';
import componentStyles from './location-map.css.lit.ts';
//import atlasStyles from './location-map-atlas.css.lit.ts';
// The location-map-atlas.css doesn't exist, so use location-map.css for now.
// Previously location-map-atlas.css.lit.ts was force-committed to the git repo.
import atlasStyles from './location-map.css.lit.ts';
import { OutlineElement } from '../../base/outline-element/outline-element.ts';

// loaded from Drupal as a workaround for now
// import * as atlas from 'azure-maps-control';

import pinDefault from './icons/pin-default.svg';
import pinSelected from './icons/pin-selected.svg';

const subscriptionKey = 'lu8s9a1qMFajkuUk2e9fsYM3UAL7du8gv7IIdcOmq1Y';

// const colorDarkPurple = '#35196C'
const colorPurple = '#6B1F99';
const colorWhite = '#ffffff';

export const baseMapConfig = {
  center: [-80.614299, 28.288154],
  zoom: 9,
  language: 'en-US',
  renderWorldCopies: false,
  showBuildingModels: false,
  showLogo: true,
  showFeedbackLink: true,
  style: 'grayscale_light',
};

/**
 * Location information from using bingmap tool
 * https://www.bing.com/api/maps/sdkrelease/mapcontrol/isdk/searchbyaddress#TS
 */

export const locationMapType = ['static', 'dynamic'];

const pinDefaultTemplate = `
<svg viewBox="0 0 89 113" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g filter="url(#filter0_d)">
    <path fill="{color}" d="M47.34 20.16c12.394 1.4 21.856 12.279 21.657 24.804-.198 12.393-10.68 18.403-16.469 29.248a83.67 83.67 0 00-7.046 18.027c-.263 1.015-1.7 1.015-1.964 0a83.674 83.674 0 00-7.046-18.027C30.628 63.263 20 57.242 20 44.608c0-14.53 12.538-26.119 27.34-24.447z"/>
    <text x="50%" y="42%" fill="{secondaryColor}" style="font-family: Lato, sans-serif" text-anchor="middle" font-size="22px" font-weight="bold" alignment-baseline="central">{scale}</text>
  </g>
  <defs>
    <filter id="filter0_d" x="0" y="0" width="89" height="113" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feColorMatrix in="SourceAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/>
      <feOffset/>
      <feGaussianBlur stdDeviation="10"/>
      <feColorMatrix values="0 0 0 0 0.419608 0 0 0 0 0.121569 0 0 0 0 0.6 0 0 0 0.46 0"/>
      <feBlend in2="BackgroundImageFix" result="effect1_dropShadow"/>
      <feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
    </filter>
  </defs>
</svg>
`;

export default class LocationMap extends OutlineElement {
  static styles = [componentStyles, atlasStyles];
  static get properties() {
    return {
      /**
       * LocationMap active point when changed will highlight the location, and jump to the coordinates
       */
      activePoint: { type: String },
      /**
       * LocationMap filters applied to rendered pins String[]
       */
      filters: { type: Array },
      /**
       * 'static' | 'dynamic'
       */
      mapType: { type: String },
      /**
       * LocationMap static image, required when mapType is set to static'
       */
      staticImage: { type: String },
      /**
       * Array of location-card items extended with { location: { longitude, latitude }, top, right, }
       */
      locationItems: { type: Array },
      mapStyling: { type: String },
    };
  }

  constructor() {
    super();
    this._selectedCard = nothing;
    this.mapStyling = 'app';
    this.mapType = 'dynamic';
    this.baseMapConfig = baseMapConfig;
    this.locationItems = [];
  }

  connectedCallback() {
    super.connectedCallback();
    this.locationListSelectMarkerHandler =
      this.locationListSelectMarker.bind(this);
    window.addEventListener(
      'hf-location-list-select',
      this.locationListSelectMarkerHandler
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(
      'hf-location-list-select',
      this.locationListSelectMarkerHandler
    );
  }

  locationListSelectMarker(event) {
    const shapeArray = this.datasource.shapes.filter(
      shapeArrayItem => shapeArrayItem.data.properties.id === event.detail.id
    );
    const shapeId = shapeArray[0].data.id;
    const shape = this.datasource.getShapeById(shapeId);
    // this doesn't work as expected :(
    // this.map.setCamera({
    //   center: new data.Position(
    //     shape.data.geometry.coordinates[0],
    //     shape.data.geometry.coordinates[1]
    //   ),
    // })
    this._handleMapIconSwap(shape);
    this.requestUpdate();
  }

  createDynamicMap() {
    const mapElement = this.shadowRoot.getElementById('health-first-map');

    this.map = new atlas.Map(mapElement, {
      ...this.baseMapConfig,
      authOptions: {
        authType: 'subscriptionKey',
        subscriptionKey,
      },
    });

    this.map.events.add('load', () => {
      window.dispatchEvent(new Event('resize'));

      if (this.mapStyling === 'block' || this.mapStyling === 'block-small') {
        const shapes = this.datasource.getShapes();

        if (shapes[0]) {
          this._selectedCard = shapes[0].data.properties.card;

          // this.map.setCamera({
          //   center: [shapes[0].data.geometry.coordinates[0], shapes[0].data.geometry.coordinates[1]],
          // })

          this.requestUpdate();
        }
      }
    });

    /**
     * Register all image sprites
     */
    this.map.events.add('ready', () => {
      const imageSpriteArray = [];
      const numberOfLocations = this.locationItems.length;

      atlas.addImageTemplate('pin-default', pinDefaultTemplate, true);

      // Just as placeholder for now so we can replace it later for a real icon
      // imageSpriteArray.push(
      //   this.map.imageSprite.createFromTemplate(
      //     'clusterIcon',
      //     'marker-flat',
      //     colorPurple,
      //     colorWhite
      //   )
      // )

      window.dispatchEvent(new Event('resize'));
      for (let i = 1; i <= numberOfLocations; i += 1) {
        imageSpriteArray.push(
          this.map.imageSprite.createFromTemplate(
            `pin-default-${i}`,
            'pin-default',
            colorWhite,
            colorPurple,
            i
          )
        );
        imageSpriteArray.push(
          this.map.imageSprite.createFromTemplate(
            `pin-selected-${i}`,
            'pin-default',
            colorPurple,
            colorWhite,
            i
          )
        );
      }

      imageSpriteArray.push(
        this.map.imageSprite.add('hf-location-primary', pinDefault)
      );

      imageSpriteArray.push(
        this.map.imageSprite.add('hf-location-selected', pinSelected)
      );

      Promise.all(imageSpriteArray).then(() => {
        this.generateMarkers();
        if (this.activePoint) {
          this._jumpToPoint(this.activePoint);
        }

        if (this.mapStyling === 'block' || this.mapStyling === 'block-small') {
          const shapes = this.datasource.getShapes();

          if (shapes[0]) {
            this._selectedCard = shapes[0].data.properties.card;
            this.requestUpdate();
            this.map.setCamera({
              center: [
                parseFloat(shapes[0].data.geometry.coordinates[0]),
                parseFloat(shapes[0].data.geometry.coordinates[1]),
                parseFloat(shapes[0].data.geometry.coordinates[0]),
                parseFloat(shapes[0].data.geometry.coordinates[1]),
              ],
            });
          }
        }
      });
    });
  }

  createCard(item) {
    let cardTitle;
    if (this.mapStyling === 'block' || this.mapStyling === 'block-small') {
      cardTitle = 'Visit ' + item.title;
    } else {
      cardTitle = item.title;
    }
    return html`<hf-location-card
      cardType="map"
      styleOverride="${this.mapStyling}"
      imageAlt="${item.imageAlt}"
      imageSrc="${item.imageSrc}"
      addressText="${item.address}"
      addressLine1="${item.address_line1}"
      addressLine2="${item.address_line2}"
      phoneNumber="${item.phone}"
      title="${cardTitle}"
      detailsLink="${item.url}"
    ></hf-location-card>`;
  }

  handleClose() {
    this._selectedCard = nothing;
    this.requestUpdate();
  }

  generateMarkers() {
    this.datasource = new atlas.source.DataSource();
    // this.datasource = new source.DataSource(null, {
    //   cluster: true,
    //   clusterRadius: 45,
    //   clusterMaxZoom: 15,
    // })
    //
    // const clusterBubbleLayer = new layer.BubbleLayer(this.datasource, null, {
    //   radius: 20,
    //   color: colorPurple,
    //   strokeWidth: 0,
    //   filter: ['has', 'point_count'],
    // })

    this.map.sources.add(this.datasource);

    this.locationItems.forEach(item => {
      const feature = new atlas.data.Feature(
        new atlas.data.Point([item.location.longitude, item.location.latitude]),
        {
          id: item.id,
          number: item.number,
          title: item.title,
          tags: item.tags,
          selected: this.mapStyling === 'block' || this.mapStyling === 'block-small',
          card: this.mapStyling === 'block' ? this.createCard(item) : null,
        }
      );
      this.datasource.add(feature);
    });

    const pinStyleApp = [
      'case',
      // ['==', ['has', 'point_count'], true],
      // ['to-string', 'none'],
      ['!=', ['get', 'selected'], false],
      ['concat', 'pin-selected-', ['to-string', ['get', 'number']]],
      ['concat', 'pin-default-', ['to-string', ['get', 'number']]],
    ];

    const pinStyleBlock = [
      'case',
      ['!=', ['get', 'selected'], false],
      'hf-location-primary',
      'hf-location-selected',
    ];

    this.symbolLayer = new atlas.layer.SymbolLayer(this.datasource, null, {
      iconOptions: {
        allowOverlap: true,
        ignorePlacement: true,
        anchor: 'bottom',
        offset: [0, 5],
        image: this.mapStyling === 'app' ? pinStyleApp : pinStyleBlock,
        // filter: ['!', ['has', 'point_count']],
        size: 0.5,
      },
      textOptions: {
        allowOverlap: true,
        ignorePlacement: true,
        offset: [0, 0.75],
        color: colorPurple,
        textField: [
          'format',

          ['get', 'title'],
          {
            'text-font': ['literal', ['StandardFont-Bold']],
            'font-scale': 1,
            'text-color': colorPurple,
          },
        ],
      },
    });

    this.map.layers.add([
      // clusterBubbleLayer,

      // new layer.SymbolLayer(this.datasource, null, {
      //   iconOptions: {
      //     image: 'none',
      //   },
      //   textOptions: {
      //     textField: ['get', 'point_count_abbreviated'],
      //     offset: [0, 0.4],
      //     color: colorWhite,
      //   },
      // }),
      this.symbolLayer,
    ]);

    this.map.events.add('click', this.symbolLayer, event => {
      if (event.shapes[0]) {
        this._selectedCard = event.shapes[0].data.properties.card;
      }
      const shape = this.datasource.getShapeById(event.shapes[0].data.id);
      const mapEvent = new CustomEvent('hf-location-map-select', {
        detail: {
          id: event.shapes[0].data.properties.id,
        },
      });
      // this.map.setCamera({
      //   center: shape.getCoordinates(),
      // });
      window.dispatchEvent(mapEvent);
      this._handleMapIconSwap(shape);
    });
  }

  _handleMapIconSwap(shape) {
    if (this._previousShape && shape.data.id !== this._previousShape.data.id) {
      this._previousShape.addProperty('selected', false);
    }
    shape.addProperty('selected', true);
    this._previousShape = shape;
    this.requestUpdate();
  }

  _jumpToPoint(point) {
    if (this.datasource && this.map) {
      // Match the properties id and return the string of the datasource layer id
      const matchedShape = this.datasource
        .getShapes()
        .find(shape => shape.data.properties.id === point.id);

      if (!matchedShape) return;

      const shape = this.datasource.getShapeById(matchedShape.data.id);
      this._handleMapIconSwap(shape);

      this.map.setCamera({
        center: [point.location.longitude, point.location.latitude],
      });
      this.requestUpdate();
    }
  }

  firstUpdated() {
    if (this.mapType !== 'static') {
      this.createDynamicMap();
    }
  }

  updated(changedProperties) {
    if (
      (changedProperties.has('filters') &&
        this.filters !== changedProperties.get('filters')) ||
      (changedProperties.has('locationItems') &&
        this.locationItems !== changedProperties.get('locationItems'))
    ) {
      this._updateFilters();
    }

    if (changedProperties.has('activePoint')) {
      if (this.activePoint) {
        this._jumpToPoint(this.activePoint);
      }
    }
  }

  _updateFilters() {
    if (!this.symbolLayer) {
      return;
    }

    const getTag = ['get', 'tags'];
    const filter = [
      Drupal.t('all'),
      ...this.filters.map(filterItem => ['in', filterItem, getTag]),
    ];
    this.symbolLayer.setOptions({
      filter,
    });
  }

  _renderStaticMap() {
    return html`<div class="location-map__static-map-container">
      <div class="location-map__static-map">
        <img
          class="location-map__map-image"
          alt="location map"
          src="${this.staticImage}"
          width="400"
          height="379"
        />
        ${this.locationItems.map(
          ({ title, top, left }) =>
            html`<div
              class="location-map__map-icon"
              style="top:${top}; left:${left};"
            >
              <hf-icon name="map-location-primary" class=""></hf-icon>
              <span class="location-map__map-icon__text">${title}</span>
            </div> `
        )}
      </div>
    </div>`;
  }

  render() {
    return html`<div class="location-map location-map--${this.mapStyling}">
      ${this.mapType === 'static'
        ? this._renderStaticMap()
        : html`<div
            id="health-first-map"
            class="location-map__map-container"
          ></div>`}
      <div class="location-map__map-card">${this._selectedCard}</div>
    </div>`;
  }
}

customElements.define('hf-location-map', LocationMap);
